import type { IProductBasic } from '@noths/polaris-client-listings-api';
import { isHighlyRated, isTopBrand } from '@noths/polaris-client-listings-api';
import { getProductImageURL } from '@noths/polaris-client-utils';
import type {
  ProductCardBadge,
  ProductCarouselItem,
} from '@noths/polaris-design-system-candidate-components';
import type { Product } from '@noths/polaris-design-system-ooux-interfaces';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { RecommendationsServiceProduct } from 'src/data/builders/recommendationsProductBuilder';

/**
 * Ordered by priority: the card has a single badge slot, so the first rule that
 * applies takes it.
 *
 * Most Loved comes first by product decision, and the data supports it. On 598
 * prod products, Top Brand is on 16% against Most Loved's 2%, so Most Loved is
 * much the more telling signal, and 43% of Most Loved products are also Top Brand,
 * meaning the other order would suppress nearly half of the Most Loved badges.
 *
 * "Most Loved" and "Top Brand" are the customer-facing names for `highly_rated`
 * and `star_brand`, matching the PLP's cards and the PDP's badge rules.
 */
const BADGE_RULES: {
  applies: (attributes: IProductBasic['attributes']) => boolean;
  badge: ProductCardBadge;
}[] = [
  { applies: isHighlyRated, badge: { icon: 'mostLoved', label: 'Most Loved' } },
  { applies: isTopBrand, badge: { icon: 'topBrand', label: 'Top Brand' } },
];

const transformBadge = ({
  attributes,
}: RecommendationsServiceProduct): ProductCardBadge | undefined =>
  BADGE_RULES.find(({ applies }) => applies(attributes))?.badge;

/**
 * Adapts a listings-api recommendation into the OOUX `Product` the design system
 * consumes.
 *
 * The interface is deliberately semantic, so several service fields have no
 * counterpart and are not mapped: `salePercentage` because `ProductCard` derives
 * the discount by comparing `price` with `originalPrice`, `onSale` because the
 * discount is the pre-sale price being present, `freeDomesticDelivery` because
 * the card expresses delivery as a cost and an arrival date, which this endpoint
 * cannot supply, and `isNew` because it is card presentation rather than a
 * property of the product (see
 * `transformRecommendationsProductToProductCarouselItem`). All of them still
 * reach GA, which reads the service product directly.
 *
 * The rating and the badge are card presentation too, so they sit on the carousel
 * item alongside the product rather than on it.
 */
export const transformRecommendationsProductToProduct = (
  {
    code,
    images,
    links,
    partner,
    preSalePrices,
    prices,
    purchasable,
    title,
  }: RecommendationsServiceProduct,
  { currency }: { currency: CurrencyCode },
): Product => {
  const price = prices.find((price) => price.currency === currency);
  const preSalePrice = preSalePrices?.find((price) => price.currency === currency);
  const [image] = images;

  return {
    /* Derived, not a constant. The request filters on the Vertex `availability`
       attribute rather than on `purchasable`, so nothing guarantees the two
       agree, and the card this replaces was given `purchasable` directly. */
    availability: purchasable ? 'in_stock' : 'out_of_stock',
    code: String(code),
    currencyCode: currency,
    /* The service returns a `{size}` token in place of a preset size, so the URL
       has to be resolved here: `ProductMedia.url` is contracted as a fully
       resolved URL and the card renders it verbatim, unlike the Ribbons card it
       replaces, which resolved the token itself. 'normal' is the size that card
       asked for, so the bytes on the wire do not change.

       Empty alt because the card wraps the image in a link named by the title,
       so describing it again would announce the product twice. */
    media: image ? [{ alt: '', type: 'image', url: getProductImageURL(image.href, 'normal') }] : [],
    /* Keyed on the pre-sale price alone, not on `onSale`. That matches the card
       this replaces, which took the pre-sale price and never received `onSale` at
       all, so a product priced down without the flag set keeps showing its
       discount. */
    ...(preSalePrice && { originalPrice: preSalePrice.amount }),
    // The card shows the partner's name as plain text. `shortcode` and `url` are
    // optional and unrendered, so they are left out until something needs them.
    partner: { name: partner.name },
    price: price ? price.amount : 0,
    title,
    /* `rel`, not `href`. The recommendations response puts the site-relative
       product path in `rel` and an absolute URL in `href` that points at a QA
       host outside production. */
    url: links[0]?.rel || '',
  };
};

/**
 * Gated on the average being above zero rather than on `ratings` being present.
 * An average of zero means unrated rather than rated zero, and would otherwise
 * render an empty star row where the card shows "NEW". Matches the PLP's own
 * `hasRating` rule.
 *
 * `ratingCount` is deliberately unused: the card shows the average alone.
 */
const transformRating = ({ ratings }: RecommendationsServiceProduct): number | undefined =>
  typeof ratings?.averageRating === 'number' &&
  Number.isFinite(ratings.averageRating) &&
  ratings.averageRating > 0
    ? ratings.averageRating
    : undefined;

/**
 * One carousel item is one card's props: the product itself plus the flags that
 * are presentation rather than properties of the product.
 *
 * `priority` is deliberately unset. The carousel is client-fetched behind
 * `LazyRender`, so it is never above the fold and its images should not pre-empt
 * ones that are.
 *
 * The badge is whichever of Most Loved and Top Brand applies first, per
 * `BADGE_RULES`. Note that Top Brand describes the partner rather than the
 * product, so a rail that happens to draw several products from one partner will
 * badge them together.
 *
 * No `delivery`, and this is a deliberate omission rather than a gap waiting to be
 * filled. The recommendations endpoint returns `delivery_cost` and `delivery_days`
 * but not the type suffixed attributes, and bare `delivery_days` is not an arrival
 * lead time: measured on prod it sits below the fastest real delivery option on
 * 220 of 299 products, by 1 to 4 days. Dating a card from it put the arrival 1 to
 * 4 days earlier than the PLP grid's own card for the same product on 74% of
 * products, promising a date no delivery option achieves. A card needs
 * `delivery_days_FREE` or `delivery_days_STANDARD`, which this endpoint does not
 * return, so until it does, or until the filter-aware resolution moves server
 * side, there is no delivery line here. See `IRecommendationsAttributes`.
 */
export const transformRecommendationsProductToProductCarouselItem = (
  product: RecommendationsServiceProduct,
  { currency }: { currency: CurrencyCode },
): ProductCarouselItem => {
  const badge = transformBadge(product);
  const rating = transformRating(product);

  return {
    ...(badge && { badge }),
    isNew: product.isNew,
    product: transformRecommendationsProductToProduct(product, { currency }),
    /* Constant, not `product.onSale`. Every discount the catalog expresses is a
       price reduction rather than a code-based offer, and the card this replaces
       styled all of them as a sale, so there is no per-product decision to make
       here. `promoteAsSale` does nothing on a product with no discount, so it does
       not need guarding. */
    promoteAsSale: true,
    ...(rating !== undefined && { rating }),
  };
};
